import { inject } from "vue";
import { storeToRefs } from "pinia";
import { useStudiesStore } from "@/store/studies";
import { useQnAStore } from "@/experiences/qna/store/qna.store";
import { useStudyUsers } from "@/store/study-users.store";
// Create a composite key for category lookup: "studyId-categoryId"
const getCategoryKey = (studyId, categoryId) => `${studyId}-${categoryId}`;
// Filters out parent categories when their subcategories are also in the list. This prevents selecting both a parent and its child in the scope.
const filterOutParentCategories = (categories) => {
    const parentKeysWithChildren = new Set(); // Build a set of parent category keys that have children in the list
    for (const category of categories) {
        if (category.parentCategoryId !== undefined && category.parentCategoryId !== null) {
            parentKeysWithChildren.add(getCategoryKey(category.studyId, category.parentCategoryId));
        }
    }
    // Keep only categories that are not parents of other categories in the list
    return categories.filter((category) => !parentKeysWithChildren.has(getCategoryKey(category.studyId, category.categoryId)));
};
// Check if a category is a parent of any subcategory that has topics selected.
const checkIfParentOfSubcategoryWithTopics = (category, topics, categories) => {
    // Use a category map for O(1) lookups instead of O(n) find operations.
    const categoryMap = new Map(categories.map((c) => [getCategoryKey(c.studyId, c.categoryId), c]));
    return topics.some((topic) => {
        const topicCategoryId = topic.categoryId ?? -1;
        const topicCategory = categoryMap.get(getCategoryKey(topic.studyId, topicCategoryId));
        // Check if the topic's category has this category as a parent (within same study)
        return topicCategory?.parentCategoryId === category.categoryId && topic.studyId === category.studyId;
    });
};
// Filter categories to only include those without directly selected topics, excluding parent categories that have subcategories with topics or that have subcategories in the list.
const getCategoriesWithoutTopics = (categories, topics) => {
    // Build a set of category keys that have topics directly selected
    const categoryKeysWithTopics = new Set();
    for (const topic of topics) {
        const topicCategoryId = topic.categoryId ?? -1;
        categoryKeysWithTopics.add(getCategoryKey(topic.studyId, topicCategoryId));
    }
    // Filter to categories without direct topics and not parents of subcategories with topics
    const categoriesWithNoTopics = categories.filter((category) => {
        const categoryKey = getCategoryKey(category.studyId, category.categoryId);
        const hasTopic = categoryKeysWithTopics.has(categoryKey);
        const hasSubcategoryWithTopics = checkIfParentOfSubcategoryWithTopics(category, topics, categories);
        return !hasTopic && !hasSubcategoryWithTopics;
    });
    // Filter out parent categories when their subcategories are also in the list
    return filterOutParentCategories(categoriesWithNoTopics);
};
export const useQuestionScope = () => {
    const { studiesMap } = storeToRefs(useStudiesStore());
    const { users } = storeToRefs(useStudyUsers());
    const { studyActivitiesMap, allTasksMap, studyDiscussionsMap, allSegmentsMap } = storeToRefs(useQnAStore());
    const i18n = inject("i18n");
    const getFiltersFromScope = (scope) => {
        const { studyIds, activityScope, taskScope, topicScope, segmentIds, userIds, categoryScope } = scope;
        const f = {
            studies: [],
            activities: [],
            tasks: [],
            categories: [],
            discussions: [],
            segments: [],
            participants: []
        };
        const studies = new Map();
        const activities = new Map();
        if (taskScope) {
            f.tasks = taskScope.reduce((acc, { studyId, activityId, taskIds }) => {
                if (!studies.has(studyId)) {
                    const study = studiesMap.value.get(studyId);
                    studies.set(studyId, study ? study : { studyId, unavailable: true });
                }
                if (!activities.has(`${studyId}_${activityId}`)) {
                    const activity = studyActivitiesMap.value.get(studyId)?.get(activityId);
                    activities.set(`${studyId}_${activityId}`, activity ? activity : { activityId, studyId, unavailable: true });
                }
                return [
                    ...acc,
                    ...taskIds.reduce((acc2, taskId) => {
                        const task = allTasksMap.value.get(studyId)?.get(activityId)?.get(taskId);
                        acc2.push(task ? task : { taskId, activityId, studyId, unavailable: true });
                        return acc2;
                    }, [])
                ];
            }, []);
        }
        if (topicScope) {
            topicScope.forEach(({ studyId, topicIds }) => {
                if (!studies.has(studyId)) {
                    const study = studiesMap.value.get(studyId);
                    studies.set(studyId, study ? study : { studyId, unavailable: true });
                }
                const studyDiscussions = studyDiscussionsMap.value.get(studyId);
                // Process topics
                if (topicIds && topicIds.length > 0) {
                    topicIds.forEach((topicId) => {
                        const topic = studyDiscussions?.topics.get(topicId);
                        f.discussions.push(topic ? { ...topic, studyId } : { topicId, studyId, unavailable: true });
                        if (topic && topic.categoryId) {
                            const category = studyDiscussions?.categories.get(topic.categoryId);
                            if (category && !f.categories.find((c) => c.categoryId === category.categoryId)) {
                                f.categories.push({ ...category, studyId });
                            }
                            // Include parent category if this is a subcategory to maintain hierarchy
                            if (category && category.parentCategoryId) {
                                const parentCategory = studyDiscussions?.categories.get(category.parentCategoryId);
                                if (parentCategory && !f.categories.find((c) => c.categoryId === parentCategory.categoryId)) {
                                    f.categories.push({ ...parentCategory, studyId });
                                }
                            }
                        }
                        else if (topic) {
                            // Add uncategorized category for topics without a categoryId
                            if (!f.categories.find((c) => c.categoryId === -1 && c.studyId === studyId)) {
                                f.categories.push({ categoryId: -1, studyId, name: i18n.t("Uncategorized Topics") });
                            }
                        }
                    });
                }
            });
        }
        if (categoryScope) {
            categoryScope.forEach(({ studyId, categoryIds }) => {
                if (!studies.has(studyId)) {
                    const study = studiesMap.value.get(studyId);
                    studies.set(studyId, study ? study : { studyId, unavailable: true });
                }
                categoryIds?.forEach((categoryId) => {
                    const exists = f.categories.find((c) => c.categoryId === categoryId && c.studyId === studyId);
                    if (!exists) {
                        const category = studyDiscussionsMap.value.get(studyId)?.categories.get(categoryId);
                        if (category) {
                            f.categories.push({ ...category, studyId });
                        }
                        else if (categoryId === -1) {
                            // Special case for uncategorized (-1)
                            f.categories.push({ categoryId: -1, studyId, name: i18n.t("Uncategorized Topics") });
                        }
                        else {
                            f.categories.push({ categoryId, studyId, unavailable: true });
                        }
                    }
                    // We are sending only subcategory data to the API, it is stored in the filters, thus, we need to add parent category as well
                    const category = studyDiscussionsMap.value.get(studyId)?.categories.get(categoryId);
                    if (category && category.parentCategoryId) {
                        const parentCategory = studyDiscussionsMap.value.get(studyId)?.categories.get(category.parentCategoryId);
                        const parentExists = f.categories.find((c) => c.categoryId === parentCategory?.categoryId && c.studyId === studyId);
                        if (parentCategory && !parentExists) {
                            f.categories.push({ ...parentCategory, studyId });
                        }
                    }
                });
            });
        }
        if (activityScope) {
            activityScope.forEach(({ studyId, activityIds }) => {
                if (!studies.has(studyId)) {
                    const study = studiesMap.value.get(studyId);
                    studies.set(studyId, study ? study : { studyId, unavailable: true });
                }
                activityIds.forEach((activityId) => {
                    const key = `${studyId}_${activityId}`;
                    if (!activities.has(key)) {
                        const activity = studyActivitiesMap.value.get(studyId)?.get(activityId);
                        activities.set(key, activity ? activity : { activityId, studyId, unavailable: true });
                    }
                });
            });
        }
        if (studyIds) {
            studyIds.forEach((studyId) => {
                if (!studies.has(studyId)) {
                    const study = studiesMap.value.get(studyId);
                    studies.set(studyId, study ? study : { studyId, unavailable: true });
                }
            });
        }
        f.studies = Array.from(studies.values());
        f.activities = Array.from(activities.values());
        f.segments = (segmentIds ?? []).reduce((acc, segmentId) => {
            const segment = allSegmentsMap.value.get(segmentId);
            acc.push(segment ? segment : { segmentId, unavailable: true });
            return acc;
        }, []);
        f.participants = (userIds ?? []).reduce((acc, userId) => {
            const user = users.value[userId];
            acc.push(user ? { ...user, name: user.displayName } : { userId, unavailable: true });
            return acc;
        }, []);
        return f;
    };
    const getScopeFromFilters = (filters) => {
        const scope = {};
        // Apply reduction logic to prevent overlapping scopes
        const { studies: reducedStudies, activities: reducedActivities, tasks: reducedTasks, discussions: reducedDiscussions, categories: reducedCategories } = reduceOverlappingScope(filters);
        // Task-level scope
        if (reducedTasks.length) {
            scope.taskScope = groupTasksByStudyActivity(reducedTasks);
        }
        // Activity-level scope (only for activities without selected tasks)
        if (reducedActivities.length) {
            scope.activityScope = groupActivitiesByStudy(reducedActivities);
        }
        // Discussion-level scope
        if (reducedDiscussions.length) {
            scope.topicScope = groupDiscussionsByStudy(reducedDiscussions);
        }
        if (reducedCategories.length) {
            const filteredCategories = getCategoriesWithoutTopics(reducedCategories, reducedDiscussions);
            if (filteredCategories.length)
                scope.categoryScope = groupCategoriesByStudy(filteredCategories);
        }
        // Study-level scope (only studies that don't have any tasks, activities, or discussions selected)
        if (reducedStudies.length) {
            scope.studyIds = reducedStudies.map((s) => s.studyId);
        }
        // User and segment scope
        if (filters.participants.length) {
            scope.userIds = filters.participants.map((p) => p.userId);
        }
        if (filters.segments.length) {
            scope.segmentIds = filters.segments.map((s) => s.segmentId);
        }
        return scope;
    };
    const reduceOverlappingScope = (filters) => {
        const taskStudyActivityPairs = new Set(filters.tasks.map((t) => `${t.studyId}-${t.activityId}`));
        // Remove activities that have tasks selected
        const reducedActivities = filters.activities.filter((activity) => !taskStudyActivityPairs.has(`${activity.studyId}-${activity.activityId}`));
        // Get studies that have tasks, activities, or discussions selected
        const studiesWithSelections = new Set();
        filters.tasks.forEach((task) => studiesWithSelections.add(task.studyId));
        filters.activities.forEach((activity) => studiesWithSelections.add(activity.studyId));
        filters.discussions.forEach((discussion) => {
            if (discussion.studyId)
                studiesWithSelections.add(discussion.studyId);
        });
        filters.categories.forEach((category) => {
            if (category.studyId)
                studiesWithSelections.add(category.studyId);
        });
        // Only include studies that don't have any tasks, activities, or discussions selected
        const reducedStudies = filters.studies.filter((study) => !studiesWithSelections.has(study.studyId));
        return {
            studies: reducedStudies,
            activities: reducedActivities,
            tasks: filters.tasks,
            discussions: filters.discussions,
            categories: filters.categories
        };
    };
    const groupActivitiesByStudy = (activities) => {
        const grouped = new Map();
        activities.forEach((activity) => {
            if (!grouped.has(activity.studyId)) {
                grouped.set(activity.studyId, []);
            }
            grouped.get(activity.studyId).push(activity.activityId);
        });
        return Array.from(grouped.entries()).map(([studyId, activityIds]) => ({
            studyId,
            activityIds
        }));
    };
    const groupTasksByStudyActivity = (tasks) => {
        const grouped = new Map();
        tasks.forEach((task) => {
            const key = `${task.studyId}-${task.activityId}`;
            if (!grouped.has(key)) {
                grouped.set(key, {
                    studyId: task.studyId,
                    activityId: task.activityId,
                    taskIds: []
                });
            }
            grouped.get(key).taskIds.push(task.taskId);
        });
        return Array.from(grouped.values());
    };
    const groupCategoriesByStudy = (categories) => {
        const grouped = new Map();
        // Add categories
        categories.forEach((category) => {
            const studyId = category.studyId;
            if (!grouped.has(studyId)) {
                grouped.set(studyId, { studyId, categoryIds: [] });
            }
            grouped.get(studyId).categoryIds.push(category.categoryId);
        });
        return Array.from(grouped.values());
    };
    const groupDiscussionsByStudy = (discussions) => {
        const grouped = new Map();
        // Add topics
        discussions.forEach((topic) => {
            const { studyId } = topic;
            if (!grouped.has(studyId)) {
                grouped.set(studyId, { studyId, topicIds: [] });
            }
            grouped.get(studyId).topicIds.push(topic.topicId);
        });
        return Array.from(grouped.values());
    };
    const getFilterCount = (filters) => {
        const { studies: reducedStudies, activities: reducedActivities, tasks: reducedTasks, discussions: reducedDiscussions, categories: reducedCategories } = reduceOverlappingScope(filters);
        const filteredCategories = getCategoriesWithoutTopics(reducedCategories, reducedDiscussions);
        return (reducedStudies.length +
            reducedActivities.length +
            reducedTasks.length +
            reducedDiscussions.length +
            filteredCategories.length +
            filters.segments.length +
            filters.participants.length);
    };
    const getStudyIdsFromScope = (scope) => {
        const studyIds = new Set();
        scope.taskScope?.forEach(({ studyId }) => studyIds.add(studyId));
        scope.activityScope?.forEach(({ studyId }) => studyIds.add(studyId));
        scope.topicScope?.forEach(({ studyId }) => studyIds.add(studyId));
        scope.categoryScope?.forEach(({ studyId }) => studyIds.add(studyId));
        scope.studyIds?.forEach((studyId) => studyIds.add(studyId));
        return studyIds;
    };
    return {
        getFiltersFromScope,
        getScopeFromFilters,
        getFilterCount,
        reduceOverlappingScope,
        getStudyIdsFromScope
    };
};
